import React, { useMemo } from 'react';
import Select from 'react-select';
import { getAllTimezones } from '../../utils/tentTimezoneUtils';
import styles from './css/UserSettings.module.css';

const BROWSER_OPTION = { value: '', label: 'Use browser timezone' };

const timezoneSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '36px',
        height: '36px',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(109, 40, 217, 0.12)' : 'none',
        borderColor: state.isFocused ? '#dcc07a' : '#e2e8f0',
        '&:hover': {
            borderColor: state.isFocused ? '#dcc07a' : '#e2e8f0',
        },
    }),
    valueContainer: provided => ({
        ...provided,
        height: '34px',
        padding: '0 12px',
    }),
    input: provided => ({
        ...provided,
        margin: 0,
        padding: 0,
        color: '#0f172a',
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
    }),
    singleValue: provided => ({
        ...provided,
        color: '#0f172a',
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
    }),
    placeholder: provided => ({
        ...provided,
        color: '#0f172a',
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
    }),
    indicatorsContainer: provided => ({
        ...provided,
        height: '34px',
    }),
    menuPortal: provided => ({
        ...provided,
        zIndex: 9999,
    }),
    option: (provided, state) => ({
        ...provided,
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
        backgroundColor: state.isSelected ? '#f4e8cc' : state.isFocused ? '#f8fafc' : '#fff',
        color: state.isSelected ? '#8a6424' : '#0f172a',
    }),
};

let cachedTimezoneOptions = null;

const getTimezoneOptions = () => {
    if (!cachedTimezoneOptions) {
        cachedTimezoneOptions = getAllTimezones().map(tz => ({ value: tz, label: tz }));
    }
    return cachedTimezoneOptions;
};

const TimezoneSelect = ({ value, onChange, name = 'timezone' }) => {
    const options = useMemo(() => {
        const all = [...getTimezoneOptions()];
        const current = (value || '').trim();
        if (current && !all.some(option => option.value === current)) {
            all.unshift({ value: current, label: current });
        }
        return [BROWSER_OPTION, ...all];
    }, [value]);

    const selectedOption = useMemo(() => {
        const current = (value || '').trim();
        if (!current) return BROWSER_OPTION;
        return options.find(option => option.value === current) || { value: current, label: current };
    }, [value, options]);

    const handleChange = option => {
        onChange({ target: { name, value: option?.value ?? '' } });
    };

    return (
        <div className={styles.timezoneSelect}>
            <Select
                inputId={name}
                name={name}
                value={selectedOption}
                onChange={handleChange}
                options={options}
                styles={timezoneSelectStyles}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                maxMenuHeight={240}
                noOptionsMessage={() => 'No matching timezones'}
            />
        </div>
    );
};

export default TimezoneSelect;
